import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { DocusealBuilder } from "@docuseal/react";
import {
  getPitchesPendingContract,
  getDocumentTemplates,
  getUserSignature,
  createPitchDocument,
  generateDocuSealToken,
  completePitchDocumentFieldSetup,
  uploadUserSignature,
  deleteUserSignature,
  playerService,
} from "../../services/api"; // Adjust path as needed
import { useSelector } from "react-redux"; // To get current user info
import { ROLES } from "../../config/roles"; // Added for role-based logic
import { useUiLanguage } from "../../components/profile/coaching-personality/useUiLanguage";
import i18n from "../../i18n";
import { getStaticTranslation } from "../../translations/registerStaticTranslations";

const PDfICon = () => {
  return (
    <svg
      width="42"
      height="40"
      viewBox="0 0 42 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M15.3486 0.930511H27.7539L40.4648 13.6414V34.4188C40.4647 36.9873 38.3829 39.069 35.8145 39.0692H15.3486C12.7801 39.069 10.6984 36.9873 10.6982 34.4188V5.5809C10.6984 3.01241 12.7801 0.930696 15.3486 0.930511Z"
        fill="white"
        stroke="#CBD0DC"
        stroke-width="1.86047"
      />
      <rect
        y="17.6741"
        width="27.907"
        height="16.2791"
        rx="3.25581"
        fill="#FF2D55"
      />
      <path
        d="M4.625 29.7673V21.9872H7.39819C8.00348 21.9872 8.50493 22.0973 8.90255 22.3177C9.30017 22.538 9.59775 22.8394 9.79529 23.2218C9.99283 23.6017 10.0916 24.0297 10.0916 24.5058C10.0916 24.9845 9.99157 25.415 9.79149 25.7974C9.59395 26.1773 9.2951 26.4787 8.89495 26.7016C8.49734 26.9219 7.99715 27.0321 7.39439 27.0321H5.48735V26.0368H7.28802C7.67044 26.0368 7.98069 25.9709 8.21875 25.8392C8.45681 25.705 8.63156 25.5227 8.743 25.2922C8.85443 25.0617 8.91015 24.7996 8.91015 24.5058C8.91015 24.212 8.85443 23.9512 8.743 23.7232C8.63156 23.4953 8.45555 23.3168 8.21495 23.1876C7.97689 23.0584 7.66285 22.9939 7.27283 22.9939H5.79886V29.7673H4.625Z"
        fill="white"
      />
      <path
        d="M13.9864 29.7673H11.4678V21.9872H14.0662C14.8285 21.9872 15.4832 22.1429 16.0302 22.4544C16.5773 22.7634 16.9964 23.2079 17.2877 23.7878C17.5814 24.3653 17.7283 25.0579 17.7283 25.8658C17.7283 26.6762 17.5802 27.3727 17.2839 27.9552C16.9901 28.5377 16.5646 28.986 16.0074 29.3C15.4503 29.6115 14.7766 29.7673 13.9864 29.7673ZM12.6416 28.7416H13.9218C14.5145 28.7416 15.007 28.6301 15.3996 28.4073C15.7922 28.1819 16.0859 27.8564 16.2809 27.431C16.476 27.003 16.5735 26.4812 16.5735 25.8658C16.5735 25.2555 16.476 24.7375 16.2809 24.3121C16.0885 23.8866 15.801 23.5637 15.4186 23.3434C15.0362 23.123 14.5613 23.0129 13.994 23.0129H12.6416V28.7416Z"
        fill="white"
      />
      <path
        d="M19.188 29.7673V21.9872H24.0126V22.9977H20.3619V25.3682H23.6669V26.3749H20.3619V29.7673H19.188Z"
        fill="white"
      />
    </svg>
  );
};

// ✅ ERROR BOUNDARY COMPONENT for DocuSeal Builder
class DocuSealErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("DocuSeal Builder Error Boundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-8 text-center bg-red-50 border border-red-200 rounded-md">
          <h3 className="text-lg font-medium text-red-800 mb-2">
            Document Editor Failed to Load
          </h3>
          <p className="text-sm text-red-600 mb-4">
            There was an error loading the document editor:{" "}
            {this.state.error?.message || "Unknown error"}
          </p>
          <button
            onClick={() => {
              this.setState({ hasError: false, error: null });
              if (this.props.onRetry) this.props.onRetry();
            }}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

const CreateContract = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const { state: routeState } = location; // For pre-filling data
  const pitchId = searchParams.get("pitchId");
  const tenderId = searchParams.get("tenderId");
  const [dealAutoSelected, setDealAutoSelected] = useState(false);

  const currentUser = useSelector((state) => state.auth.user); // Get current user from Redux
  // QAV5-502: window.alert() is a native browser dialog the DOM scanner
  // cannot reach — route it through the same static-bundle lookup the
  // scanner uses before it goes to the native dialog.
  const currentLanguage = useUiLanguage();
  const tr = (s) => getStaticTranslation(i18n, s, currentLanguage) || s;

  const [currentStep, setCurrentStep] = useState(1);

  // Step 1: Deal and Document Source
  const [availableDeals, setAvailableDeals] = useState([]);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [resolvedPlayerByWs, setResolvedPlayerByWs] = useState(null);
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [contractSource, setContractSource] = useState("upload");
  const [documentTitle, setDocumentTitle] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [showDealModal, setShowDealModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [contractSourceSelected, setContractSourceSelected] = useState(false);

  // Step 2: Signers
  const CURRENT_USER_SIGNER_ID = "currentUserGeneratedId";
  const PITCH_CREATOR_SIGNER_ID = "pitchCreatorGeneratedId";
  const [signers, setSigners] = useState([]);
  const [addMeAsSigner, setAddMeAsSigner] = useState(false);
  const [addPitchCreatorAsSigner, setAddPitchCreatorAsSigner] = useState(false);
  const [userSignatureStatus, setUserSignatureStatus] = useState({
    hasSignature: false,
    signatureUrl: null,
    loading: true,
  });
  const [showSignaturePrompt, setShowSignaturePrompt] = useState(false);

  // Step 3: DocuSeal Field Placement
  const [pitchDocumentId, setPitchDocumentId] = useState(null);
  const [currentDocuSealTemplateId, setCurrentDocuSealTemplateId] =
    useState(null);
  const [docusealToken, setDocusealToken] = useState(null);
  const [docusealHost, setDocusealHost] = useState(null);
  const [docusealBuilderKey, setDocusealBuilderKey] = useState(0);

  // Step 4: Message/Review
  const [message, setMessage] = useState(
    "Hi, please sign the documents attached."
  );
  const [finalizingContract, setFinalizingContract] = useState(false);

  // Loading and Error States
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Initial data fetching and resume logic
  useEffect(() => {
    const fetchInitialDataAndHandleResume = async () => {
      setIsLoading(true);
      setError(null);
      let fetchedDeals = [];
      let fetchedTemplates = [];

      try {
        const dealsResponse = await getPitchesPendingContract({
          usage_context: "contract_initiation",
        });
        fetchedDeals = dealsResponse.data?.results || [];
        setAvailableDeals(fetchedDeals);
        console.log("fetchedDeals",fetchedDeals, pitchId, tenderId);
        const ourDeal = fetchedDeals.find(
          (d) => d.id == pitchId
        );
        console.log("ourDeal", ourDeal);
        if (ourDeal) {
          setSelectedDeal(ourDeal);
          setDealAutoSelected(true);
        }

        const templatesResponse = await getDocumentTemplates();
        fetchedTemplates = templatesResponse.data?.results || [];
        setAvailableTemplates(fetchedTemplates);

        const signatureResponse = await getUserSignature();
        setUserSignatureStatus({
          hasSignature: !!signatureResponse.data?.signature_image_s3_url,
          signatureUrl: signatureResponse.data?.signature_image_s3_url,
          loading: false,
        });

        // Resume Logic
        if (routeState?.resumeMode) {
          const {
            pitchDocumentIdToResume,
            dealToResume,
            templateIdToResume,
            signersToResume,
            documentTitleToResume,
          } = routeState;

          if (dealToResume) {
            setSelectedDeal(dealToResume);
            if (templateIdToResume) {
              const originalTemplate = fetchedTemplates.find(
                (t) => t.docuseal_template_id === templateIdToResume
              );
              if (originalTemplate) {
                setSelectedTemplate(originalTemplate);
                setContractSource("template");
              } else {
                setContractSource("upload");
              }
            } else {
              setContractSource("upload");
            }
          }
          if (documentTitleToResume) setDocumentTitle(documentTitleToResume);
          if (pitchDocumentIdToResume)
            setPitchDocumentId(pitchDocumentIdToResume);
          if (templateIdToResume)
            setCurrentDocuSealTemplateId(templateIdToResume);

          if (signersToResume) {
            const currentUserId = currentUser?.id;
            const pitchCreatorEmail = dealToResume?.created_by?.email;

            let willAddMe = false;
            let willAddCreator = false;

            const mappedSigners = signersToResume.map((s) => {
              let internalId = s.id;
              let isCurrentUserFlag = false;
              if (s.email === currentUser?.email) {
                internalId = CURRENT_USER_SIGNER_ID;
                isCurrentUserFlag = true;
                willAddMe = true;
              } else if (s.email === pitchCreatorEmail) {
                internalId = PITCH_CREATOR_SIGNER_ID;
                isCurrentUserFlag = false;
                willAddCreator = true;
              }
              return {
                id: internalId,
                name: s.name,
                email: s.email,
                role_placeholder: s.role_placeholder,
                is_current_user: isCurrentUserFlag,
              };
            });
            setSigners(mappedSigners);
            if (willAddMe) setAddMeAsSigner(true);
            if (willAddCreator && currentUser?.email !== pitchCreatorEmail)
              setAddPitchCreatorAsSigner(true);
          }

          // Navigate to Step 3: DocuSeal Field Placement
          if (pitchDocumentIdToResume && templateIdToResume) {
            try {
              const tokenResponse = await generateDocuSealToken({
                action: "build",
                docuseal_template_id: templateIdToResume,
              });
              if (tokenResponse.data?.token) {
                setDocusealToken(tokenResponse.data.token);
                setDocusealHost(tokenResponse.data.docuseal_host);
                setDocusealBuilderKey((prev) => prev + 1);
                setCurrentStep(3);
              } else {
                throw new Error(
                  "Failed to generate Docuseal token for resume or host missing."
                );
              }
            } catch (resumeTokenError) {
              console.error(
                "Error generating Docuseal token for resume:",
                resumeTokenError
              );
              setError(
                resumeTokenError.message ||
                  "Could not prepare document editor for resume. Please try again."
              );
              setCurrentStep(1);
            }
          } else {
            setError(
              "Missing critical information (document or template ID) to resume contract at field placement step."
            );
            setCurrentStep(1);
          }
        } else {
          // Normal pre-fill logic if not resuming
          if (routeState?.dealId && fetchedDeals.length > 0) {
            const preSelectedDeal = fetchedDeals.find(
              (d) => d.id === routeState.dealId
            );
            if (preSelectedDeal) setSelectedDeal(preSelectedDeal);
          }
          if (routeState?.templateId && fetchedTemplates.length > 0) {
            const preSelectedTemplate = fetchedTemplates.find(
              (t) => t.id === routeState.templateId
            );
            if (preSelectedTemplate) {
              setSelectedTemplate(preSelectedTemplate);
              setContractSource("template");
              setDocumentTitle(preSelectedTemplate.name || "");
            }
          }
        }
      } catch (err) {
        console.error(
          "Error fetching initial data or processing resume state:",
          err
        );
        setError(
          "Failed to load initial data or resume contract. Please try again."
        );
        setUserSignatureStatus({
          hasSignature: false,
          signatureUrl: null,
          loading: false,
        });
      }
      setIsLoading(false);
    };
    fetchInitialDataAndHandleResume();
  }, [routeState, currentUser, pitchId, tenderId]);

  // When a deal is selected but player fields are missing, resolve by Wyscout ID
  useEffect(() => {
    const wsId = selectedDeal?.player?.wyscout_id || selectedDeal?.player_wyscout_id;
    const hasPlayerName = Boolean(selectedDeal?.player?.name);

    if (!selectedDeal || !wsId || hasPlayerName || resolvedPlayerByWs) {
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const resp = await playerService.getPublicPlayerProfile(wsId);
        if (!cancelled && resp?.success) {
          setResolvedPlayerByWs(resp.data);
          // Merge minimal fields into selectedDeal without breaking shape
          setSelectedDeal((prev) =>
            prev
              ? {
                  ...prev,
                  player: {
                    ...(prev.player || {}),
                    name: prev.player?.name || resp.data?.name,
                    image: prev.player?.image || resp.data?.image,
                    position: prev.player?.position || resp.data?.position,
                    country: prev.player?.country || resp.data?.country,
                  },
                }
              : prev
          );
        }
      } catch (e) {
        // Silent fallback
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedDeal, resolvedPlayerByWs]);

  // Effect to manage "Add me as signer"
  useEffect(() => {
    setSigners((prevSigners) => {
      const meExists = prevSigners.some((s) => s.id === CURRENT_USER_SIGNER_ID);
      if (addMeAsSigner && currentUser) {
        const myRole = "Club Representative";
        if (!meExists) {
          return [
            {
              id: CURRENT_USER_SIGNER_ID,
              name: currentUser.first_name || currentUser.name || "Me",
              email: currentUser.email,
              role_placeholder: myRole,
              is_current_user: true,
            },
            ...prevSigners.filter(
              (s) =>
                s.id !== PITCH_CREATOR_SIGNER_ID ||
                s.email !== currentUser.email
            ),
          ];
        } else {
          return prevSigners.map((s) =>
            s.id === CURRENT_USER_SIGNER_ID
              ? {
                  ...s,
                  name: currentUser.first_name || currentUser.name || "Me",
                  email: currentUser.email,
                  role_placeholder: myRole,
                  is_current_user: true,
                }
              : s
          );
        }
      } else if (!addMeAsSigner && meExists) {
        return prevSigners.filter((s) => s.id !== CURRENT_USER_SIGNER_ID);
      }
      return prevSigners;
    });
  }, [addMeAsSigner, currentUser]);

  // Effect to manage "Add pitch creator as signer"
  useEffect(() => {
    setSigners((prevSigners) => {
      const pitchCreatorExists = prevSigners.some(
        (s) => s.id === PITCH_CREATOR_SIGNER_ID
      );
      const pitchCreatorUser = selectedDeal?.created_by;

      if (
        addPitchCreatorAsSigner &&
        pitchCreatorUser?.email &&
        pitchCreatorUser.email !== currentUser?.email
      ) {
        let pitchCreatorRolePlaceholder = "Pitch Creator";
        const pitchCreatorRoles = pitchCreatorUser.roles || [];
        if (
          pitchCreatorRoles.includes(ROLES.AGENT) ||
          pitchCreatorRoles.includes(ROLES.AGENCY_ADMIN)
        ) {
          pitchCreatorRolePlaceholder = "Agent";
        }

        if (!pitchCreatorExists) {
          return [
            {
              id: PITCH_CREATOR_SIGNER_ID,
              name:
                pitchCreatorUser.name ||
                pitchCreatorUser.first_name ||
                "Pitch Creator",
              email: pitchCreatorUser.email,
              role_placeholder: pitchCreatorRolePlaceholder,
              is_current_user: false,
            },
            ...prevSigners.filter(
              (s) =>
                s.id !== CURRENT_USER_SIGNER_ID ||
                s.email !== pitchCreatorUser.email
            ),
          ];
        } else {
          return prevSigners.map((s) =>
            s.id === PITCH_CREATOR_SIGNER_ID
              ? {
                  ...s,
                  name:
                    pitchCreatorUser.name ||
                    pitchCreatorUser.first_name ||
                    "Pitch Creator",
                  email: pitchCreatorUser.email,
                  role_placeholder: pitchCreatorRolePlaceholder,
                  is_current_user: false,
                }
              : s
          );
        }
      } else if (!addPitchCreatorAsSigner && pitchCreatorExists) {
        return prevSigners.filter((s) => s.id !== PITCH_CREATOR_SIGNER_ID);
      }
      return prevSigners;
    });
  }, [addPitchCreatorAsSigner, selectedDeal, currentUser]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (
      file?.type === "application/pdf" ||
      file?.type ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ) {
      setUploadedFile({
        file,
        name: file.name,
        size: (file.size / (1024 * 1024)).toFixed(2),
      });
      if (!documentTitle && contractSource === "upload") {
        setDocumentTitle(file.name.replace(/\.[^/.]+$/, ""));
      }
    } else {
      alert(tr("Please upload a PDF or DOCX file."));
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (
      file?.type === "application/pdf" ||
      file?.type ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ) {
      setUploadedFile({
        file,
        name: file.name,
        size: (file.size / (1024 * 1024)).toFixed(2),
      });
      if (!documentTitle && contractSource === "upload") {
        setDocumentTitle(file.name.replace(/\.[^/.]+$/, ""));
      }
    } else {
      alert(tr("Please upload a PDF or DOCX file."));
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleAddSigner = () => {
    if (signers.length < 5) {
      setSigners((prevSigners) => [
        ...prevSigners,
        {
          id: Date.now(),
          name: "",
          email: "",
          role_placeholder: `Signer ${
            prevSigners.filter(
              (s) =>
                s.id !== CURRENT_USER_SIGNER_ID &&
                s.id !== PITCH_CREATOR_SIGNER_ID
            ).length + 1
          }`,
          is_current_user: false,
        },
      ]);
    } else {
      alert(tr("Maximum of 5 signers allowed."));
    }
  };

  const handleRemoveSigner = (idToRemove) => {
    if (idToRemove === CURRENT_USER_SIGNER_ID) {
      setAddMeAsSigner(false);
    } else if (idToRemove === PITCH_CREATOR_SIGNER_ID) {
      setAddPitchCreatorAsSigner(false);
    }
    setSigners((prevSigners) =>
      prevSigners.filter((signer) => signer.id !== idToRemove)
    );
  };

  const handleSignerDetailChange = (id, field, value) => {
    setSigners(
      signers.map((signer) =>
        signer.id === id ? { ...signer, [field]: value } : signer
      )
    );
  };

  const handleNext = async () => {
    setError(null);

    if (currentStep === 1) {
      if (!selectedDeal) {
        setError("Please select a deal.");
        return;
      }
      if (!documentTitle.trim()) {
        setError("Please enter a document title.");
        return;
      }
      if (contractSource === "upload" && !uploadedFile) {
        setError("Please upload a document file.");
        return;
      }
      if (contractSource === "template" && !selectedTemplate) {
        setError("Please select a template.");
        return;
      }
      setCurrentStep(2);
    } else if (currentStep === 2) {
      const manuallyAddedSigners = signers.filter(
        (s) =>
          s.id !== CURRENT_USER_SIGNER_ID && s.id !== PITCH_CREATOR_SIGNER_ID
      );
      const emptyManualSigner = manuallyAddedSigners.find(
        (s) => !s.email.trim() || !s.name.trim()
      );

      if (
        manuallyAddedSigners.length === 0 &&
        !addMeAsSigner &&
        !addPitchCreatorAsSigner
      ) {
        setError("Please add at least one signer.");
        return;
      }
      if (emptyManualSigner) {
        setError(
          "Please fill in all manually added signer names and email addresses."
        );
        return;
      }
      if (
        addMeAsSigner &&
        !userSignatureStatus.hasSignature &&
        !showSignaturePrompt
      ) {
        setShowSignaturePrompt(true);
        return;
      }

      setIsLoading(true);
      try {
        const signersPayload = signers.map((s) => ({
          email: s.email,
          name: s.name,
          role_placeholder: s.role_placeholder,
          is_current_user: s.is_current_user || false,
        }));

        // Debug: Verify the payload being sent for signer creation
        console.log("Signers payload for createPitchDocument:", signersPayload);

        const pitchDocumentData = {
          pitch_id: selectedDeal.id,
          document_title: documentTitle,
          source_template_id:
            contractSource === "template" ? selectedTemplate.id : null,
          signers: signersPayload,
        };

        // Debug: Inspect the non-multipart request body
        console.log("Pitch document request body:", pitchDocumentData);

        let response;
        if (contractSource === "upload" && uploadedFile) {
          const formData = new FormData();
          formData.append("pitch_id", selectedDeal.id);
          formData.append("document_title", documentTitle);
          formData.append("uploaded_document_file", uploadedFile.file);
          formData.append("signers", JSON.stringify(signersPayload));
          response = await createPitchDocument(formData);
        } else {
          response = await createPitchDocument(pitchDocumentData);
        }

        console.log("Create Pitch Document Response:", response);

        if (response.data) {
          const createdDoc = response.data;
          setPitchDocumentId(createdDoc.id);

          const dsTemplateId =
            contractSource === "upload"
              ? createdDoc?.docuseal_template_id
              : selectedTemplate?.docuseal_template_id;

          if (!dsTemplateId) {
            throw new Error(
              "Docuseal Template ID not found in API response or selected template."
            );
          }
          setCurrentDocuSealTemplateId(dsTemplateId);

          const tokenResponse = await generateDocuSealToken({
            action: "build",
            docuseal_template_id: dsTemplateId,
          });

          if (tokenResponse.data?.token && tokenResponse.data?.docuseal_host) {
            setDocusealToken(tokenResponse.data.token);
            setDocusealHost(tokenResponse.data.docuseal_host);
            setDocusealBuilderKey((prev) => prev + 1);
            setCurrentStep(3);
          } else {
            throw new Error(
              "Failed to generate Docuseal token or host missing."
            );
          }
        } else {
          throw new Error(
            response.error ||
              "Failed to create pitch document or invalid response."
          );
        }
      } catch (err) {
        console.error("Error in handleNext (Step 2 to 3 transition):", err);
        setError(
          err.message ||
            "An error occurred while preparing the document. Please try again."
        );
      }
      setIsLoading(false);
    } else if (currentStep === 3) {
      // Complete field setup and initiate DocuSeal submission
      setIsLoading(true);
      try {
        const completeResponse = await completePitchDocumentFieldSetup(
          pitchDocumentId
        );
        if (completeResponse.data) {
          // Field setup is complete and DocuSeal has already sent invitations.
          // Redirect user back to the document manager with success flag.
          navigate("/documents", {
            state: { contractCreated: true, documentId: pitchDocumentId },
          });
        } else {
          throw new Error(
            completeResponse.error || "Failed to confirm field setup."
          );
        }
      } catch (err) {
        console.error("Error completing field setup:", err);
        setError(err.message || "An error occurred while saving field setup.");
      }
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setError(null);
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      navigate(-1);
    }
  };

  // --- Render Step 1: Select Deal and Document Source ---
  const renderSelectDealAndDocumentStep = () => {
    if (
      userSignatureStatus.loading ||
      (isLoading &&
        currentStep === 1 &&
        !availableDeals.length &&
        !availableTemplates.length)
    ) {
      return <div className="p-8 text-center">Loading initial data...</div>;
    }
    // Show general loading for other operations
    if (isLoading && currentStep === 1) {
      return <div className="p-8 text-center">Loading...</div>;
    }

    return (
      <>
        <div className="bg-white rounded-lg p-8 border border-[#D6DBEA]">
          <div className="flex items-start space-x-4 mb-8">
            <div className="flex-none">
              <div className="w-8 h-8 rounded-full bg-[#F8F9FB] flex items-center justify-center text-[#374DF5] font-bold text-lg">
                1
              </div>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Select Deal & Contract details
              </h2>
            </div>
          </div>

          {error && currentStep === 1 && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
              <p>{error}</p>
            </div>
          )}

          <div className="bg-[#F8F9FB] px-10 py-4">
            {selectedDeal ? (
              <div className="mb-6">
                <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-[#E5E7EB]">
                  <div className="flex items-center space-x-4 w-[90%] justify-between">
                    <span className="text-sm font-medium text-[#1A212F] border-r border-[#D6DBEA] pr-4">
                      Selected deal:
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-[#646B7E]">
                        Position:
                      </span>
                      <span className="text-sm text-[#000000] font-medium">
                        {(selectedDeal.tender_position || selectedDeal.player_position)
                          ? (selectedDeal.tender_position || selectedDeal.player_position)
                              .replace(/_/g, " ")
                              .split(" ")
                              .map(
                                (word) =>
                                  word.charAt(0).toUpperCase() + word.slice(1)
                              )
                              .join(" ")
                          : "N/A"}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">
                        Type:
                      </span>
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          selectedDeal.tender_type === "buy"
                            ? "bg-green-100 text-green-700"
                            : selectedDeal.tender_type === "full_time"
                            ? "bg-purple-100 text-purple-700"
                            : "bg-blue-100 text-blue-700"
                        }`}
                      >
                        {selectedDeal.tender_type === "buy"
                          ? "Buy"
                          : selectedDeal.tender_type === "full_time"
                          ? "Full Time"
                          : selectedDeal.tender_type || "N/A"}
                      </span>
                    </div>

                    {selectedDeal.tender_category === "staff" ? (
                      // Staff deal display
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">
                          Staff:
                        </span>
                        <div className="flex items-center space-x-2">
                          <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center">
                            <span className="text-xs font-medium text-purple-600">
                              {selectedDeal.staff_name?.[0] || "S"}
                            </span>
                          </div>
                          <span className="text-sm text-purple-600">
                            {selectedDeal.staff_name || "Staff Member"}
                          </span>
                        </div>
                      </div>
                    ) : (
                      // Player deal display (existing)
                      <>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900">
                            Player:
                          </span>
                          <div className="flex items-center space-x-2">
                            <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                              <span className="text-xs font-medium text-blue-600">
                                {selectedDeal.player?.name?.[0] || "M"}
                              </span>
                            </div>
                            <span className="text-sm text-blue-600">
                              {selectedDeal.player?.name || "N/A"}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-[#646B7E]">
                            Agency:
                          </span>
                          <div className="flex items-center space-x-2">
                            <div className="w-6 h-6 bg-gray-800 rounded-full flex items-center justify-center">
                              <span className="text-xs font-medium text-white">
                                P
                              </span>
                            </div>
                            <span className="text-sm text-[#000000] font-medium">
                              {selectedDeal.agency_name || "N/A"}
                            </span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  <button
                    disabled={dealAutoSelected}
                    onClick={() => setShowDealModal(true)}
                    className="p-1 text-gray-400 w-[10%] flex justify-end"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            ) : (
              <div className="">
                <span className="text-[#000000] text-sm font-medium block mb-2">
                  Select deal:
                </span>
                <span className="text-[#000000CC] text-sm block mb-4">
                  Select the player and agency involved in the deal. This will
                  link the contract to the correct transfer.
                </span>

                <div
                  disabled={dealAutoSelected}
                  onClick={() => setShowDealModal(true)}
                  className="px-4 py-2 w-fit rounded-lg border border-[#E6E6E6] bg-white text-[#1E1E1E] cursor-pointer"
                >
                  Select a deal
                </div>
              </div>
            )}

            <div className="mt-4">
              <span className="text-[#000000] text-sm font-medium block mb-2">
                Add Contract details:
              </span>
              <span className="text-[#000000CC] text-sm block mb-4">
                Choose how you'd like to begin: use a saved template or upload
                your own contract.
              </span>
              <div className="flex gap-3 items-center mb-4">
                <div
                  onClick={() => {
                    setContractSource("upload");
                    setContractSourceSelected(true);
                  }}
                  className={`px-4 py-2 w-fit rounded-lg border ${
                    contractSource === "upload" && contractSourceSelected
                      ? "border-[#2283FF] bg-[#EDF3FB] text-[#2283FF]"
                      : "border-[#E6E6E6] bg-white text-[#1E1E1E]"
                  } cursor-pointer`}
                >
                  Upload new
                </div>

                <div
                  onClick={() => {
                    setContractSource("template");
                    setContractSourceSelected(true);
                  }}
                  className={`px-4 py-2 w-fit rounded-lg border ${
                    contractSource === "template" && contractSourceSelected
                      ? "border-[#2283FF] bg-[#EDF3FB] text-[#2283FF]"
                      : "border-[#E6E6E6] bg-white text-[#1E1E1E]"
                  } cursor-pointer`}
                >
                  Use a template
                </div>
              </div>

              {contractSource === "upload" &&
                contractSourceSelected &&
                !uploadedFile && (
                  <div
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    className="border border-dashed border-[#D6DBEA] my-4 bg-white w-fit p-8 rounded-lg"
                  >
                    <div className="flex gap-3 items-center mb-4">
                      <span className="block text-[#292D32] text-base font-medium">
                        Choose a file or drag & drop it here
                      </span>
                      <span className="block text-[#1A212F99] text-base font-medium">
                        PDFs only, up to 50MB
                      </span>
                    </div>

                    <label className="mt-4 px-4 py-2 rounded-lg bg-[#2283FF] text-[#F5F5F5] cursor-pointer">
                      <input
                        type="file"
                        accept=".pdf,.docx"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                      Select file
                    </label>
                  </div>
                )}

              {uploadedFile && (
                <>
                  <div className="flex justify-between px-4 py-3 rounded-xl items-center border border-[#2283FF] bg-[#EDF3FB] mb-4">
                    <div className="flex gap-3 items-center">
                      <span className="text-[#1A212F] text-base font-medium">
                        File uploaded:
                      </span>
                      <span>{PDfICon()}</span>
                      <div className="flex flex-col gap-1">
                        <span className="text-[#292D32] text-sm font-medium">
                          {uploadedFile.name}
                        </span>
                        <span className="text-[#A9ACB4] text-sm">
                          {uploadedFile.size}mb
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center px-4 py-2 rounded-lg justify-center bg-[#3EBF8F1A]">
                      <span>
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M12 2C6.49 2 2 6.49 2 12C2 17.51 6.49 22 12 22C17.51 22 22 17.51 22 12C22 6.49 17.51 2 12 2ZM16.78 9.7L11.11 15.37C10.97 15.51 10.78 15.59 10.58 15.59C10.38 15.59 10.19 15.51 10.05 15.37L7.22 12.54C6.93 12.25 6.93 11.77 7.22 11.48C7.51 11.19 7.99 11.19 8.28 11.48L10.58 13.78L15.72 8.64C16.01 8.35 16.49 8.35 16.78 8.64C17.07 8.93 17.07 9.4 16.78 9.7Z"
                            fill="#3EBF8F"
                          />
                        </svg>
                      </span>
                      <span className="text-[#292D32] text-sm font-medium">
                        Upload Completed
                      </span>
                    </div>
                  </div>

                  <div
                    className="px-4 py-2 rounded-lg bg-white w-fit border border-[#FF2D5580] text-[#FF3B30] mb-4 cursor-pointer "
                    onClick={() => {
                      setUploadedFile(null);
                    }}
                  >
                    Remove & Upload new file
                  </div>
                </>
              )}

              {contractSource === "template" && contractSourceSelected && (!selectedTemplate || selectedTemplate.isPreSelected) && (
                <div className="border border-[#D6DBEA] rounded-lg bg-white">
                  <div className="px-6 py-4 w-full text-[#0A0F2D] text-sm font-medium bg-[#FAFAFA]">
                    Available templates
                  </div>
                  <div className="px-6 py-4">
                    <span className="block text-[#242426B2] text-xs mb-2">
                      Title
                    </span>

                    <div className="overflow-y-auto max-h-64">
                      <div className="overflow-x-auto">
                        <table className="w-full table-sticky-first">
                          <thead className="sticky top-0 bg-white">
                            <tr className="border-b border-gray-200">
                              <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">
                                Title
                              </th>
                              <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">
                                Contract type
                              </th>
                              <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">
                                Player position
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {availableTemplates.map((template) => (
                              <tr
                                key={template.id}
                                className={`hover:bg-gray-50 cursor-pointer py-2 ${
                                  selectedTemplate?.id === template.id
                                    ? "bg-blue-50 border-[#2283FF] border"
                                    : "border-b border-gray-100"
                                }`}
                                onClick={() => {
                                  // Just highlight the row but don't select the template yet
                                  setSelectedTemplate({...template, isPreSelected: true});
                                }}
                              >
                                <td className="py-3 px-4">
                                  <div className="flex items-center space-x-2">
                                    <div className="w-6 h-6 bg-blue-100 rounded flex items-center justify-center">
                                      <svg
                                        className="w-4 h-4 text-blue-600"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                        />
                                      </svg>
                                    </div>
                                    <span className="text-sm text-gray-900">
                                      {template.name}
                                    </span>
                                  </div>
                                </td>
                                <td className="py-3 px-4">
                                  <span
                                    className={`px-2 py-1 text-xs font-medium rounded-full ${
                                      template.template_type === "buy"
                                        ? "bg-green-100 text-green-700"
                                        : template.template_type === "loan"
                                        ? "bg-yellow-100 text-yellow-700"
                                        : "bg-blue-100 text-blue-700"
                                    }`}
                                  >
                                    {template.template_type === "buy"
                                      ? "Buy"
                                      : template.template_type === "loan"
                                      ? "Loan"
                                      : template.template_type || "Buy"}
                                  </span>
                                </td>
                                <td className="py-3 px-4">
                                  <span className="text-sm text-gray-900">
                                    {template.player_position ||
                                      "Head of scout"}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div className="flex justify-end space-x-3 mt-4">
                      <button
                        onClick={() => {
                          setContractSourceSelected(false);
                        }}
                        className="px-4 py-2 bg-white border border-[#E6E6E6] rounded-lg text-[#1E1E1E]"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => {
                          if (selectedTemplate) {
                            // Remove the isPreSelected flag and set the document title
                            const { isPreSelected, ...templateData } = selectedTemplate;
                            setSelectedTemplate(templateData);
                            setDocumentTitle(templateData.name || "");
                          }
                        }}
                        disabled={!selectedTemplate}
                        className="px-4 py-2 bg-[#2283FF] text-[#F5F5F5] rounded-lg disabled:opacity-50"
                      >
                        Confirm selection
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              {contractSource === "template" && contractSourceSelected && selectedTemplate && !selectedTemplate.isPreSelected && (
                <div className="mb-6">
                  <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-[#E5E7EB]">
                    <div className="flex items-center space-x-4 w-[90%]">
                      <span className="text-sm font-medium text-[#1A212F] border-r border-[#D6DBEA] pr-4">
                        Selected template:
                      </span>
                      <div className="flex items-center space-x-2">
                        <div className="w-6 h-6 bg-blue-100 rounded flex items-center justify-center">
                          <svg
                            className="w-4 h-4 text-blue-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                        </div>
                        <span className="text-sm text-gray-900">
                          {selectedTemplate.name}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">
                          Type:
                        </span>
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${
                            selectedTemplate.template_type === "buy"
                              ? "bg-green-100 text-green-700"
                              : selectedTemplate.template_type === "loan"
                              ? "bg-yellow-100 text-yellow-700"
                              : "bg-blue-100 text-blue-700"
                          }`}
                        >
                          {selectedTemplate.template_type === "buy"
                            ? "Buy"
                            : selectedTemplate.template_type === "loan"
                            ? "Loan"
                            : selectedTemplate.template_type || "Buy"}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedTemplate(null)}
                      className="p-1 text-gray-400 w-[10%] flex justify-end"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Select Deal Section */}
          {!selectedDeal && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select deal:
              </label>
              <p className="text-sm text-gray-600 mb-4">
                Select the player and agency involved in the deal. This will
                link the contract to the correct transfer.
              </p>
              <button
                onClick={() => setShowDealModal(true)}
                className="w-full p-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-gray-400 hover:text-gray-600 transition-colors"
              >
                Select a deal
              </button>
            </div>
          )}
        </div>

        {/* Deal Selection Modal */}
        {showDealModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Available deals
                </h3>
              </div>
              <div className="p-6 overflow-y-auto max-h-96">
                <div className="overflow-x-auto">
                  <table className="w-full table-sticky-first">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">
                          Player/Staff
                        </th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">
                          Agency
                        </th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">
                          Position
                        </th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">
                          Deal type
                        </th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">
                          Deal status
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {availableDeals.map((deal) => (
                        <tr
                          key={deal.id}
                          className={`border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${
                            selectedDeal?.id === deal.id ? "bg-blue-50" : ""
                          }`}
                          onClick={() => {
                            setSelectedDeal(deal);
                            setShowDealModal(false);
                          }}
                        >
                          <td className="py-3 px-4">
                            {deal.tender_category === "staff" ? (
                              // Staff display
                              <div className="flex items-center space-x-2 flex-col sm:flex-row gap-2">
                                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                                  <span className="text-sm font-medium text-purple-600">
                                    {deal.staff_name?.[0] || "S"}
                                  </span>
                                </div>
                                <span className="text-sm text-gray-900">
                                  {deal.staff_name || "Staff Member"}
                                </span>
                              </div>
                            ) : (
                              // Player display (existing)
                              <div className="flex items-center space-x-2 flex-col sm:flex-row gap-2">
                                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                  <span className="text-sm font-medium text-blue-600">
                                    {deal.player?.name?.[0] || "M"}
                                  </span>
                                </div>
                                <span className="text-sm text-gray-900">
                                  {deal.player?.name || "N/A"}
                                </span>
                              </div>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            {deal.tender_category === "staff" ? (
                              // Staff deals don't have agency
                              <span className="text-sm text-gray-500">-</span>
                            ) : (
                              // Player agency display (existing)
                              <div className="flex items-center space-x-2 ">
                                <div className="w-8 h-8 bg-gray-800 rounded-full flex items-center justify-center">
                                  <span className="text-sm font-medium text-white">
                                    P
                                  </span>
                                </div>
                                <span className="text-sm text-gray-900">
                                  {deal.agency_name || "N/A"}
                                </span>
                              </div>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-sm text-gray-900">
                              {(deal.tender_position || deal.player_position)
                                ? (deal.tender_position || deal.player_position)
                                    .replace(/_/g, " ")
                                    .split(" ")
                                    .map(
                                      (word) =>
                                        word.charAt(0).toUpperCase() +
                                        word.slice(1)
                                    )
                                    .join(" ")
                                : "N/A"}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span
                              className={`px-2 py-1 text-xs font-medium rounded-full ${
                                deal.tender_type === "buy"
                                  ? "bg-green-100 text-green-700"
                                  : deal.tender_type === "full_time"
                                  ? "bg-purple-100 text-purple-700"
                                  : "bg-blue-100 text-blue-700"
                              }`}
                            >
                              {deal.tender_type === "buy"
                                ? "Buy"
                                : deal.tender_type === "full_time"
                                ? "Full Time"
                                : deal.tender_type || "N/A"}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-sm text-green-600 font-medium">
                              Accepted
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
                <button
                  onClick={() => setShowDealModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setShowDealModal(false)}
                  disabled={!selectedDeal}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  Confirm selection
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  const renderSignersStep = () => {
    // Loading state for user signature check if relevant
    if (userSignatureStatus.loading && addMeAsSigner) {
      return (
        <div className="p-8 text-center">Checking your signature status...</div>
      );
    }

    return (
      <div className="bg-white rounded-lg p-8">
        <div className="flex items-center space-x-4 mb-8">
          <div className="flex-none">
            <div className="w-8 h-8 rounded-full bg-[#F8F9FB] flex items-center justify-center text-[#374DF5] font-bold text-lg">
              2
            </div>
          </div>
          <div>
            <h2 className="text-[#202630] text-lg font-bold">Add Signers</h2>
            <p className="text-[#000000CC] text-sm mt-1">
              Enter the signers' details, and we'll email a signature invitation
              to all invitees.
            </p>
          </div>
        </div>

        {error && currentStep === 2 && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
            <p>{error}</p>
          </div>
        )}

        {/* Modal to prompt for signature creation */}
        {showSignaturePrompt && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Signature Required
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                You've chosen to add yourself as a signer, but you don't have a
                signature saved yet. Please create your signature to proceed.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowSignaturePrompt(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowSignaturePrompt(false);
                    navigate("/documents/edit-signature", {
                      state: {
                        from: location.pathname,
                        context: "createContract",
                      },
                    });
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                >
                  Create Signature
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="p-6 border-2 border-dashed border-gray-200 rounded-lg bg-[#F8F9FB]">
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <span className="text-base font-medium text-[#000000]">
                  Add me as a signer
                </span>
                <button
                  onClick={() => {
                    setAddMeAsSigner(!addMeAsSigner);
                  }}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                    addMeAsSigner ? "bg-blue-600" : "bg-gray-200"
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      addMeAsSigner ? "translate-x-5" : "translate-x-0"
                    }`}
                  />
                </button>
              </div>
            </div>

            {addMeAsSigner && (
              <p className="text-sm text-[#00000099]">
                We'll add your signature in this document while sending it to
                other invitees.
              </p>
            )}

            {/* Pitch Creator as Signer Option */}
            {selectedDeal?.created_by?.email &&
              selectedDeal.created_by.email !== currentUser?.email && (
                <div className="flex items-center justify-between mt-4">
                  <div className="flex items-center space-x-3">
                    <span className="text-base font-medium text-[#000000]">
                      Add pitch creator (
                      {selectedDeal.created_by.name ||
                        selectedDeal.created_by.first_name ||
                        "Pitch Creator"}{" "}
                      - {selectedDeal.created_by.email}) as a signer
                    </span>
                    <button
                      onClick={() => {
                        setAddPitchCreatorAsSigner(!addPitchCreatorAsSigner);
                      }}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                        addPitchCreatorAsSigner ? "bg-blue-600" : "bg-gray-200"
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          addPitchCreatorAsSigner
                            ? "translate-x-5"
                            : "translate-x-0"
                        }`}
                      />
                    </button>
                  </div>
                </div>
              )}
            {addPitchCreatorAsSigner &&
              selectedDeal?.created_by?.email &&
              selectedDeal.created_by.email !== currentUser?.email && (
                <p className="text-sm text-[#00000099]">
                  The pitch creator will be added as a signer.
                </p>
              )}

            {addMeAsSigner &&
              !userSignatureStatus.hasSignature &&
              !userSignatureStatus.loading && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700 mb-3">
                    Looks like you don't have a signature yet. Create your
                    signature below to proceed with the contract.
                  </p>
                  <button
                    onClick={() => {
                      navigate("/documents/edit-signature", {
                        state: {
                          from: location.pathname,
                          context: "createContract",
                        },
                      });
                    }}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg"
                  >
                    Create signature
                  </button>
                </div>
              )}

            {/* Signer Inputs */}
            <div className="space-y-4">
              {signers.map((signer, index) => (
                <div key={signer.id} className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="block text-base font-medium text-[#000000]">
                      {signer.id === CURRENT_USER_SIGNER_ID
                        ? "Your Details (as Signer)"
                        : signer.id === PITCH_CREATOR_SIGNER_ID
                        ? `Pitch Creator Details (as Signer)`
                        : signer.role_placeholder || "Signer Details"}
                    </label>
                    {signer.id !== CURRENT_USER_SIGNER_ID &&
                      signer.id !== PITCH_CREATOR_SIGNER_ID &&
                      signers.filter(
                        (s) =>
                          s.id !== CURRENT_USER_SIGNER_ID &&
                          s.id !== PITCH_CREATOR_SIGNER_ID
                      ).length > 0 && (
                        <button
                          onClick={() => handleRemoveSigner(signer.id)}
                          className="text-sm text-red-600 hover:text-red-700"
                        >
                          Remove Signer
                        </button>
                      )}
                  </div>

                  <div className="space-y-3">
                    <input
                      type="email"
                      value={signer.email}
                      onChange={(e) =>
                        handleSignerDetailChange(
                          signer.id,
                          "email",
                          e.target.value
                        )
                      }
                      placeholder="Email address"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      readOnly={
                        signer.id === CURRENT_USER_SIGNER_ID ||
                        signer.id === PITCH_CREATOR_SIGNER_ID
                      }
                    />
                    <input
                      type="text"
                      value={signer.name}
                      onChange={(e) =>
                        handleSignerDetailChange(
                          signer.id,
                          "name",
                          e.target.value
                        )
                      }
                      placeholder="Full name"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      readOnly={
                        signer.id === CURRENT_USER_SIGNER_ID ||
                        signer.id === PITCH_CREATOR_SIGNER_ID
                      }
                    />
                    <input
                      type="text"
                      value={signer.role_placeholder}
                      onChange={(e) =>
                        handleSignerDetailChange(
                          signer.id,
                          "role_placeholder",
                          e.target.value
                        )
                      }
                      placeholder="Role (e.g., Club Representative)"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      readOnly={
                        signer.id === CURRENT_USER_SIGNER_ID ||
                        signer.id === PITCH_CREATOR_SIGNER_ID
                      }
                    />
                  </div>
                </div>
              ))}
            </div>

            {signers.filter(
              (s) =>
                s.id !== CURRENT_USER_SIGNER_ID &&
                s.id !== PITCH_CREATOR_SIGNER_ID
            ).length < 3 && ( // Max 5 total, 2 are programmatic
              <button
                onClick={handleAddSigner}
                className="w-fit px-4 py-2 rounded-lg border border-[#E6E6E6] bg-white text-[#1E1E1E] cursor-pointer"
              >
                Add another signer
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderSignatureFieldsStep = () => {
    if (!docusealToken || !currentDocuSealTemplateId) {
      return (
        <div className="p-8 text-center">
          Preparing document editor... Please wait.
        </div>
      );
    }
    if (isLoading && currentStep === 3) {
      return <div className="p-8 text-center">Loading Document Editor...</div>;
    }

    // DocuSeal Builder expects an array of role identifiers, not a comma-separated string
    const docusealRoles = signers.map((signer) => signer.role_placeholder);

    // Prepare field configuration - only signature fields for each signer role
    const fieldConfiguration = signers.map((signer, index) => {
      const willBePrefilled =
        signer.is_current_user && userSignatureStatus.hasSignature;

      // Always use 'signature' field type in builder to avoid showing image URL; backend will auto-fill.
      return {
        type: "signature",
        name: `${signer.role_placeholder
          .toLowerCase()
          .replace(/\s+/g, "_")}_signature`,
        role: signer.role_placeholder,
        required: true,
      };
    });

    // DocuSeal React component expects an array, not a JSON string
    const fieldsArrayForBuilder = fieldConfiguration;

    return (
      <div className="bg-white rounded-lg p-8">
        <div className="flex items-center space-x-4 mb-8">
          <div className="flex-none">
            <div className="w-8 h-8 rounded-full bg-[#F8F9FB] flex items-center justify-center text-[#374DF5] font-medium">
              3
            </div>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-[#202630]">
              Position Signature fields
            </h2>
            <p className="text-sm text-[#20263099] mt-1">
              Drag and drop signature fields to where each invitee needs to
              sign.
            </p>
          </div>
        </div>

        {error && currentStep === 3 && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
            <p>{error}</p>
          </div>
        )}

        {/* Signer Cards with Prefill Indicators */}
        <div className="flex space-x-4 mb-6">
          {signers.map((signer, index) => {
            const willBePrefilled =
              signer.is_current_user && userSignatureStatus.hasSignature;

            return (
              <div
                key={signer.id}
                className={`p-3 border-2 rounded-lg bg-[#F8F9FB] ${
                  willBePrefilled
                    ? "border-green-300 bg-green-50"
                    : "border-dashed border-gray-300"
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs text-gray-500">
                    {index + 1}
                    {getOrdinalSuffix(index + 1)} Signer
                  </div>
                  {willBePrefilled && (
                    <div className="flex items-center space-x-1">
                      <svg
                        className="w-3 h-3 text-green-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="text-xs font-medium text-green-600">
                        Will auto-sign
                      </span>
                    </div>
                  )}
                </div>

                <div className="text-xs font-medium text-gray-700 mb-1">
                  {signer.role_placeholder || "Signer"}
                </div>

                <div className="flex items-center space-x-2">
                  <div
                    className={`w-6 h-6 rounded flex items-center justify-center ${
                      willBePrefilled ? "bg-green-600" : "bg-blue-600"
                    }`}
                  >
                    <span className="text-xs font-medium text-white">
                      {willBePrefilled ? "✓" : "QES"}
                    </span>
                  </div>
                  <div>
                    <div
                      className={`text-xs font-medium ${
                        willBePrefilled ? "text-green-600" : "text-blue-600"
                      }`}
                    >
                      {willBePrefilled
                        ? "Auto-signature enabled"
                        : "Qualified electronic signature - Swiss law"}
                    </div>
                    <div className="text-xs text-gray-500">{signer.email}</div>
                  </div>
                </div>

                {willBePrefilled && (
                  <div className="mt-2 p-2 bg-green-100 rounded text-xs text-green-700">
                    <strong>Repositionable:</strong> Position this signature
                    field where you want your saved signature to appear.
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Info banner about repositioning
        {signers.some(
          (s) => s.is_current_user && userSignatureStatus.hasSignature
        ) && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <div className="flex items-start space-x-2">
              <svg
                className="w-4 h-4 text-blue-600 mt-0.5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                <p className="text-sm font-medium text-blue-800">
                  Signature Position Control
                </p>
                <p className="text-sm text-blue-700">
                  You can reposition ALL signature fields below, including the
                  one that will use your saved signature. Your signature will be
                  automatically applied to the position you choose.
                </p>
              </div>
            </div>
          </div>
        )} */}

        {/* DocuSeal Builder Container */}
        <div className="min-h-[600px] border border-gray-300 rounded-md overflow-hidden relative bg-white">
          {/* <div className="p-4 bg-blue-50 border-b border-blue-200">
            <p className="text-sm text-blue-700">
              <strong>Document Editor</strong> - Position signature fields where
              each signer should sign
            </p>
          </div> */}

          <div style={{ width: "100%", height: "600px", position: "relative" }}>
            {DocusealBuilder ? (
              <DocuSealErrorBoundary
                onRetry={() => setDocusealBuilderKey((prev) => prev + 1)}
              >
                <DocusealBuilder
                  key={`docuseal-${docusealBuilderKey}-${currentDocuSealTemplateId}`}
                  token={docusealToken}
                  {...(docusealHost && docusealHost !== "app.docuseal.com"
                    ? { host: docusealHost.replace(/^https?:\/\//, "") }
                    : {})}
                  language="en"
                  withTitle={false}
                  withUploadButton={false}
                  withAddPageButton={false}
                  withSendButton={true}
                  withSignYourselfButton={false}
                  roles={docusealRoles}
                  fields={fieldsArrayForBuilder}
                  onlyDefinedFields={true}
                  fieldTypes={["signature", "image"]}
                  withFieldPlaceholder={true}
                  onError={(event) => {
                    console.error("DocuSeal Builder Error:", event);
                    setError(
                      `Document Editor Error: ${
                        event?.detail?.message ||
                        event?.message ||
                        "Unknown error"
                      }. Please refresh or try again.`
                    );
                  }}
                  style={{
                    width: "100%",
                    height: "600px",
                    border: "none",
                    display: "block",
                  }}
                />
              </DocuSealErrorBoundary>
            ) : (
              <div className="p-8 text-center bg-red-50 border border-red-200 rounded-md">
                <h3 className="text-lg font-medium text-red-800 mb-2">
                  DocuSeal Component Not Available
                </h3>
                <p className="text-sm text-red-600 mb-4">
                  The DocuSeal React component could not be loaded. Please check
                  your installation.
                </p>
                <div className="flex space-x-2 justify-center">
                  <button
                    onClick={() => setDocusealBuilderKey((prev) => prev + 1)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Retry Load
                  </button>
                  <button
                    onClick={() => window.location.reload()}
                    className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                  >
                    Refresh Page
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <p className="text-xs text-gray-500 mt-2">
          Position signature fields for each signer role. Auto-signatures will
          be applied to the positions you choose when the document is sent.
        </p>
      </div>
    );
  };

  const renderMessageStep = () => (
    <div className="bg-white rounded-lg p-8">
      <div className="flex items-start space-x-4 mb-8">
        <div className="flex-none">
          <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-medium">
            4
          </div>
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Send Invite</h2>
          <p className="text-sm text-gray-600 mt-1">
            Click to send the signature request to your invitees.
          </p>
        </div>
      </div>

      {error && currentStep === 4 && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
          <p>{error}</p>
        </div>
      )}

      <div className="space-y-6">
        <div>
          <label
            htmlFor="final-message"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Message (Optional)
          </label>
          <textarea
            id="final-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={8}
            className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm resize-none"
            placeholder="Add a message for your invitees..."
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-12 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleBack}
                className="flex items-center text-sm font-medium text-gray-600 hover:text-gray-900  gap-3"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12.8346 6.99999H1.16797M1.16797 6.99999L7.0013 12.8333M1.16797 6.99999L7.0013 1.16666"
                    stroke="#2C2C2C"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>

                <span className="text-[#202630] text-xl font-bold">
                  Create new contract
                </span>
              </button>
            </div>

            <div className="flex items-center space-x-3">
              <button
                onClick={handleBack}
                className="px-4 py-2 border border-[#E6E6E6] bg-white rounded-lg shadow-sm text-[#1E1E1E]"
                disabled={isLoading || finalizingContract}
              >
                Cancel
              </button>
              <button
                onClick={handleNext}
                className="px-4 py-2 disabled:bg-[#2283FF80] bg-[#2283FF] text-white rounded-lg"
                disabled={
                  isLoading ||
                  finalizingContract ||
                  (currentStep === 1 &&
                    (!selectedDeal ||
                      (contractSource === "upload" && !uploadedFile) ||
                      (contractSource === "template" && !selectedTemplate) ||
                      !documentTitle.trim())) ||
                  (currentStep === 2 &&
                    ((signers.filter(
                      (s) =>
                        s.id !== CURRENT_USER_SIGNER_ID &&
                        s.id !== PITCH_CREATOR_SIGNER_ID
                    ).length === 0 &&
                      !addMeAsSigner &&
                      !addPitchCreatorAsSigner) ||
                      signers
                        .filter(
                          (s) =>
                            s.id !== CURRENT_USER_SIGNER_ID &&
                            s.id !== PITCH_CREATOR_SIGNER_ID
                        )
                        .some((s) => !s.email.trim() || !s.name.trim()))) ||
                  (currentStep === 3 && !pitchDocumentId) // Basic check for step 3 readiness
                }
              >
                {(isLoading && currentStep !== 4) ||
                (finalizingContract && currentStep === 4) ? (
                  <span className="flex items-center">
                    <svg
                      className="animate-spin -ml-1 mr-3 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Processing...
                  </span>
                ) : currentStep === 3 ? (
                  "Send Invites & Finish"
                ) : (
                  "Next"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="px-12 py-8">
        {currentStep === 1 && renderSelectDealAndDocumentStep()}
        {currentStep === 2 && renderSignersStep()}
        {currentStep === 3 && renderSignatureFieldsStep()}
        {/* Step 4 removed – flow completes in step 3 */}
      </div>
    </div>
  );
};

const getOrdinalSuffix = (number) => {
  const suffixes = ["th", "st", "nd", "rd"];
  const v = number % 100;
  return suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0];
};

export default CreateContract;
